import { useEffect, useState } from "react"
import Description from "./Description"
import Detail from "./Detail"
import Gallery from "./Gallery"
import RelatedProduct from "./RelatedProduct"
import { ProductDetailItemProps, ProductVariantItemProps } from "./constants"
import { NextSeo, ProductJsonLd } from "next-seo"
import { useAttributesStore } from "@/stores/useAttributesStore"
import { AttributeValueItem } from "@/lib/constants"
import { Offers } from "next-seo/lib/types"
import { event } from 'nextjs-google-analytics'


const Product = (props: ProductDetailItemProps) => {
  const { getAttributes } = useAttributesStore();
  const attributesData = getAttributes();
  const [variantId, setVariantId] = useState(0);
  const [variant, setVariant] = useState<ProductVariantItemProps | null>(null);
  const [refresh, setRefresh] = useState(false);
  const [firstVariant, setFirstVariant] = useState<ProductVariantItemProps | null>(null);
  const [init, setInit] = useState(true);
  const [colors, setColors] = useState<Array<AttributeValueItem>>([])
  const [sizes, setSizes] = useState<Array<AttributeValueItem>>([])
  const [stages, setStages] = useState<Array<AttributeValueItem>>([])
  const [patterns, setPatterns] = useState<Array<AttributeValueItem>>([])
  const [genders, setGenders] = useState<Array<AttributeValueItem>>([])
  const [flavours, setFlavours] = useState<Array<AttributeValueItem>>([])
  const [selectColor, setSelectColor] = useState<number>(0)
  const [selectSize, setSelectSize] = useState<number>(0)
  const [selectStage, setSelectStage] = useState<number>(0)
  const [selectPattern, setSelectPattern] = useState<number>(0)
  const [selectGender, setSelectGender] = useState<number>(0)
  const [selectFlavour, setSelectFlavour] = useState<number>(0)
  const [additionalImages, setAdditionalImages] = useState<Array<string>>([])
  const [offerVariants, setOfferVariants] = useState<Array<Offers>>([])

  useEffect(() => {
    const parser_attributes = async () => {
      setAdditionalImages([]);
      setOfferVariants([]);

      const priceValidDate = new Date(new Date().getTime() + 5 * 24 * 60 * 60 * 1000);
      const priceValidUntil = `${priceValidDate.getFullYear()}-${(priceValidDate.getMonth() + 1).toString().padStart(2, '0')}-${priceValidDate.getDate().toString().padStart(2, '0')}`

      try {
        for (var i = 0; i < attributesData.data.length; i++) {
          const filtered = attributesData.data[i].attribute_values.filter((attribute) => props.attribute_value_list.some((value) => attribute.id === value)).sort((a, b) => (a.order === b.order) ? a.id - b.id : a.order - b.order);

          switch (attributesData.data[i].slug) {
            case "color":
              setColors(filtered);

              if (filtered.length > 0) {
                setSelectColor(filtered[0].id);
              }
              break;

            case "size":
              setSizes(filtered);

              if (filtered.length > 0) {
                setSelectSize(filtered[0].id);
              }
              break;

            case "stage":
              setStages(filtered);

              if (filtered.length > 0) {
                setSelectStage(filtered[0].id);
              }
              break;

            case "pattern":
              setPatterns(filtered);

              if (filtered.length > 0) {
                setSelectPattern(filtered[0].id);
              }
              break;

            case "gender":
              setGenders(filtered);

              if (filtered.length > 0) {
                setSelectGender(filtered[0].id);
              }
              break;

            case "flavour":
              setFlavours(filtered);

              if (filtered.length > 0) {
                setSelectFlavour(filtered[0].id);
              }
              break;
          }
        }
      } finally {
        props.image_gallery.slice(1).map((image) => {
          if (image && image.image && image.image !== undefined) {
            setAdditionalImages(prevImages => [...prevImages, image.image]);
          }
        });

        props.product_variant_list.map((v) => {
          const color = colors.find((c) => v.attribute_value_list.includes(c.id));
          const size = sizes.find((s) => v.attribute_value_list.includes(s.id));
          const stage = stages.find((s) => v.attribute_value_list.includes(s.id));
          const pattern = patterns.find((p) => v.attribute_value_list.includes(p.id));
          const gender = genders.find((g) => v.attribute_value_list.includes(g.id));
          const flavour = flavours.find((f) => v.attribute_value_list.includes(f.id));

          const ov = {
            price: (v.special_price > 0 ? v.special_price : v.sales_price).toString(),
            priceValidUntil: priceValidUntil,
            priceCurrency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD', // Provide a default value to ensure it is always a string
            url: process.env.NEXT_PUBLIC_HOSTNAME + "/product/" + props.slug,
            availability: v.qty > 0 ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            itemCondition: "https://schema.org/NewCondition",
            seller: {
              name: "Green Parenting"
            },
            ...({ name: props.name }),
            ...({ sku: v.product_code }),
            ...(color !== undefined && { color: color.base_color !== null && color.base_color.length > 0 ? color.base_color : color.name }),
            ...(size !== undefined && { size: size.name }),
            ...(stage !== undefined && { stage: stage.name }),
            ...(pattern !== undefined && { pattern: pattern.name }),
            ...(gender !== undefined && { gender: gender.name }),
            ...(flavour !== undefined && { flavour: flavour.name }),
          }

          setOfferVariants(prevVariants => [...prevVariants, ov]);
        })

        event("view_item", {
          category: "ecommerce",
          currency: process.env.NEXT_PUBLIC_CURRENCY_CODE || 'USD',
          value: props.sales_price * props.qty,
          items: [
            {
              item_id: props.product_code,
              item_name: props.name,
              brand: props.brand,
            }
          ]
        });
      }
    }

    if (init) {
      parser_attributes();

      setInit(false);
    }
  }, [props.attribute_value_list, attributesData, variant, init, props.image_gallery, additionalImages, props.product_variant_list, props.slug, props.name, colors, sizes, stages, patterns, genders, flavours]);

  useEffect(() => {
    const attr = [selectColor, selectSize, selectStage, selectPattern, selectGender, selectFlavour].filter((value) => value !== 0).sort((a, b) => a - b)
    const variants = props.product_variant_list.filter((variant) => variant.attribute_value_list.sort((a, b) => a - b).toString() == attr.toString())

    setFirstVariant(variants[0]);
  }, [selectColor, selectSize, selectStage, selectPattern, selectGender, selectFlavour, props.product_variant_list, setFirstVariant])

  return (
    <>
      <NextSeo
        title=''
        defaultTitle={props.meta.title}
        description={props.meta.description}
        canonical={process.env.NEXT_PUBLIC_HOSTNAME + "/product/" + props.slug}
        openGraph={{
          images: [{
            url: props.image_gallery.length > 0 ? props.image_gallery[0].image : process.env.NEXT_PUBLIC_STATIC_SITE + "/images/brand/logo-mooimom-middle.png",
            width: 800,
            height: 800,
            alt: props.name + " image",
          }]
        }}
      />
      <ProductJsonLd
        productName={props.name}
        images={[props.image_gallery[0].image]}
        description={props.meta.description}
        brand={props.brand}
        sku={props.product_code}
        offers={offerVariants.length > 0 ? offerVariants : offerVariants[0]}
        additional_images={additionalImages.length > 0 ? additionalImages : undefined}
      />
      <div className="container grid grid-cols-1 gap-0 my-8 md:my-0 md:py-12 md:grid-cols-2 md:gap-10">
        <Gallery
          id={props.id}
          name={props.name}
          msrp={props.msrp}
          sales_price={props.sales_price}
          product_code={props.product_code}
          qty={props.qty}
          ranking={props.ranking}
          attribute_value_list={props.attribute_value_list}
          categories_list={props.categories_list}
          product_variant_list={props.product_variant_list}
          image_gallery={props.image_gallery}
          description_list={props.description_list}
          description_media_list={props.description_media_list}
          people_say={props.people_say}
          our_pick={props.our_pick}
          meta={props.meta}
          size_guide={props.size_guide}
          product_label_right_product_page={props.product_label_right_product_page}
          product_label_left_product_page={props.product_label_left_product_page}
          urls={props.urls}
          special_price={props.special_price}
          brand={props.brand}
          slug={props.slug}
          variantId={variantId}
        />
        <Detail
          id={props.id}
          name={props.name}
          msrp={props.msrp}
          sales_price={props.sales_price}
          product_code={props.product_code}
          qty={props.qty}
          ranking={props.ranking}
          attribute_value_list={props.attribute_value_list}
          categories_list={props.categories_list}
          product_variant_list={props.product_variant_list}
          image_gallery={props.image_gallery}
          description_list={props.description_list}
          description_media_list={props.description_media_list}
          people_say={props.people_say}
          our_pick={props.our_pick}
          meta={props.meta}
          size_guide={props.size_guide}
          product_label_right_product_page={props.product_label_right_product_page}
          product_label_left_product_page={props.product_label_left_product_page}
          urls={props.urls}
          special_price={props.special_price}
          brand={props.brand}
          slug={props.slug}
          setVariantId={setVariantId}
        />
      </div>
      <div className="container">
        <Description
          id={props.id}
          name={props.name}
          msrp={props.msrp}
          sales_price={props.sales_price}
          product_code={props.product_code}
          qty={props.qty}
          ranking={props.ranking}
          attribute_value_list={props.attribute_value_list}
          categories_list={props.categories_list}
          product_variant_list={props.product_variant_list}
          image_gallery={props.image_gallery}
          description_list={props.description_list}
          description_media_list={props.description_media_list}
          people_say={props.people_say}
          our_pick={props.our_pick}
          meta={props.meta}
          size_guide={props.size_guide}
          product_label_right_product_page={props.product_label_right_product_page}
          product_label_left_product_page={props.product_label_left_product_page}
          urls={props.urls}
          special_price={props.special_price}
          brand={props.brand}
          slug={props.slug}
        />
      </div>
      <div className="container">
        <RelatedProduct
          id={props.id}
          name={props.name}
          msrp={props.msrp}
          sales_price={props.sales_price}
          product_code={props.product_code}
          qty={props.qty}
          ranking={props.ranking}
          attribute_value_list={props.attribute_value_list}
          categories_list={props.categories_list}
          product_variant_list={props.product_variant_list}
          image_gallery={props.image_gallery}
          description_list={props.description_list}
          description_media_list={props.description_media_list}
          people_say={props.people_say}
          our_pick={props.our_pick}
          meta={props.meta}
          size_guide={props.size_guide}
          product_label_right_product_page={props.product_label_right_product_page}
          product_label_left_product_page={props.product_label_left_product_page}
          urls={props.urls}
          special_price={props.special_price}
          brand={props.brand}
          slug={props.slug}
        />
      </div>
    </>
  )
}

export default Product
